import { useEffect, useRef } from "react";

import { offset } from "@floating-ui/dom";
import { faSparkle } from "@fortawesome/pro-regular-svg-icons";

import {
  useFloatingWindowsActions,
  useIsFloatingWindowOpen,
} from "src/base-components/FloatingWindow/hooks";
import { Icon } from "src/design-system/Icon";
import { useUnifiedCopilotDisplay } from "src/unifiedCopilot/UnifiedCopilotDisplayProvider";

import "src/unifiedCopilot/unifiedCopilotLauncher.css";

export const UNIFIED_COPILOT_WINDOW_ID = "unified-copilot";

/**
 * The anchor div stays mounted while the window is open even though the button
 * does not: the window resolves its position from this ref after it
 * mounts, so the anchor has to be kept.
 */
export const UnifiedCopilotLauncher: React.FC = () => {
  const { open: openFloatingWindow, close: closeFloatingWindow } =
    useFloatingWindowsActions();
  const isOpen = useIsFloatingWindowOpen(UNIFIED_COPILOT_WINDOW_ID);
  const { surface, open: openCopilot } = useUnifiedCopilotDisplay();
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (surface === "floating" && !isOpen) {
      openFloatingWindow({
        id: UNIFIED_COPILOT_WINDOW_ID,
        type: "unified-copilot",
        target: anchorRef,
        positionOptions: {
          // Above the launcher, right edges aligned, so the window fills the
          // bottom-right region of the viewport.
          placement: "top-end",
          middleware: [offset({ mainAxis: 8 })],
        },
      });
      return;
    }

    if (surface !== "floating" && isOpen) {
      closeFloatingWindow(UNIFIED_COPILOT_WINDOW_ID);
    }
  }, [closeFloatingWindow, isOpen, openFloatingWindow, surface]);

  return (
    <div
      ref={anchorRef}
      className="pointer-events-none fixed right-12 z-50"
      data-loc="unified-copilot-launcher-anchor"
      style={{ bottom: 34, height: 42, width: 119 }}
    >
      {surface === "hidden" && (
        <button
          aria-label="Ask agent"
          className="unified-copilot-launcher group pointer-events-auto absolute right-0 flex h-full cursor-pointer items-center justify-center overflow-hidden rounded-full bg-indigo-600 font-inter-medium-13px text-white shadow-sm transition-all duration-200 ease-out hover:gap-0.5 hover:pr-4 hover:pl-3 focus-visible:gap-0.5 focus-visible:pr-4 focus-visible:pl-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 focus-visible:outline-offset-2 motion-reduce:transition-none"
          data-loc="unified-copilot-launcher"
          type="button"
          onClick={openCopilot}
        >
          <Icon icon={faSparkle} size="xs" variant="inverted" />
          <span
            aria-hidden="true"
            className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 ease-out group-hover:max-w-full group-hover:opacity-100 group-focus-visible:max-w-full group-focus-visible:opacity-100 motion-reduce:transition-none"
          >
            Ask agent
          </span>
        </button>
      )}
    </div>
  );
};
